import SignupBg from "@/assets/images/signupbg.png";
import { NavbarDataType } from "@/shared/data/navbar.data";
import { getCDNURL } from "@/shared/utils/commons";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { ReactNode, useEffect, useRef } from "react";
const MOBILE_BREAKPOINT = 768; 
const isMobile = typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;

// ── Types ─────────────────────────────────────────────────────────────────────

export type SignupRightPanelTestimonial = {
  avatarUrl: string | StaticImageData;
  quote: string;
  name: string;
  badge: string;
  badgeIcon: "youtube" | "linkedin";
};

export type SignupRightPanelData = {
  headingPrefix: string;
  headingHighlight: string;
  subheading: string;
  bulletPoints: string[];
  trustedAvatars: StaticImageData[];
  testimonial: SignupRightPanelTestimonial;
};

export type SignupPageLayoutProps = {
  navbarData: NavbarDataType;
  leftContent: ReactNode;
  rightPanelData: SignupRightPanelData;
  mobileBannerContent?: ReactNode;
  isUCECountry?: boolean;
  version?: "v1" | "v2";
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
    <circle cx="8" cy="8" r="8" fill="#393F41" />
    <path d="M4.5 8L6.8 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIconGreen = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
    <circle cx="8" cy="8" r="8" fill="#22C55E" />
    <path d="M4.5 8L6.8 10.5L11.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const YoutubeIcon = () => (
  <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="shrink-0">
    <path
      d="M13.5 1.56A1.76 1.76 0 0 0 12.26.3C11.18 0 7 0 7 0S2.82 0 1.74.3A1.76 1.76 0 0 0 .5 1.56 18.5 18.5 0 0 0 .21 5c-.01 1.15.09 2.3.29 3.44A1.76 1.76 0 0 0 1.74 9.7C2.82 10 7 10 7 10s4.18 0 5.26-.3a1.76 1.76 0 0 0 1.24-1.26c.2-1.14.3-2.29.29-3.44.01-1.15-.09-2.3-.29-3.44ZM5.6 7.14V2.86L9.27 5 5.6 7.14Z"
      fill="#FF0000"
    />
  </svg>
);

const LinkedinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0">
    <rect width="14" height="14" rx="2.5" fill="#0A66C2" />
    <path
      d="M3 5.25H4.75V11H3V5.25ZM3.875 4.375C3.322 4.375 2.875 3.928 2.875 3.375C2.875 2.822 3.322 2.375 3.875 2.375C4.428 2.375 4.875 2.822 4.875 3.375C4.875 3.928 4.428 4.375 3.875 4.375ZM5.75 5.25H7.5V6.125C7.813 5.5 8.5 5.125 9.25 5.125C10.769 5.125 11.25 6.063 11.25 7.563V11H9.5V7.938C9.5 7.25 9.188 6.75 8.5 6.75C7.813 6.75 7.5 7.25 7.5 7.938V11H5.75V5.25Z"
      fill="white"
    />
  </svg>
);

// ── V1 Right panel (testimonial) ──────────────────────────────────────────────

const SignupRightPanelV1 = ({ data }: { data: SignupRightPanelData }) => {
  const { headingPrefix, headingHighlight, subheading, bulletPoints, testimonial, trustedAvatars } = data;

  return (
    <div className="flex w-full flex-col gap-[30px] md:gap-[150px]">
      <div className="flex w-full flex-col gap-[18px]">
       <h2 className={`w-full text-center font-inter text-[19.34px] font-semibold leading-[24.87px] tracking-[-0.025em] md:text-left ${isMobile ? "pt-[20px]" : ""}`}>
          <span className="text-[#232228]">{headingPrefix}</span>
          <span className="inline-block text-[#FF905B] bg-clip-text font-semibold italic pr-[4px]">
            {headingHighlight}
          </span>
        </h2>
        <div className="hidden w-full flex-col items-center gap-[18px] md:flex">
          <p className="w-full text-center font-inter text-[14px] font-semibold leading-[30.72px] tracking-[-0.025em] text-[#232228] md:text-[18px] md:font-medium">
            {subheading}
          </p>
          <ul className="inline-flex flex-col gap-0">
            {bulletPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-[10px]">
                <span className="mt-[7px] shrink-0"><CheckIcon /></span>
                <span className="font-inter text-[14px] font-normal leading-[30.72px] tracking-[-0.025em] text-[#393F41]">
                  {point}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Testimonial card */}
      <div className="mx-auto w-full max-w-[286px] rounded-[10.5px] border border-[#E5E5E5] bg-white p-[12px] md:mx-0 md:max-w-none md:rounded-none md:border-0 md:bg-transparent md:p-0">
        {/* Mobile layout: row (avatar + name/badge) then quote */}
        <div className="flex flex-col gap-[10.5px] md:hidden">
          <div className="flex items-center gap-[12px]">
            <Image
              src={testimonial.avatarUrl}
              alt={testimonial.name}
              width={48}
              height={48}
              className="h-[48px] w-[48px] shrink-0 rounded-full object-cover"
              unoptimized
            />
            <div className="flex flex-col gap-[4px]">
              <span className="font-inter text-[14px] font-medium leading-[20px] text-[#111827]">
                {testimonial.name}
              </span>
              <div className="flex w-fit items-center gap-[6px] rounded-[55px]">
                {testimonial.badgeIcon === "youtube" ? <YoutubeIcon /> : <LinkedinIcon />}
                <span className="font-inter text-[12px] font-normal leading-[18px] text-[#393F41]">
                  {testimonial.badge}
                </span>
              </div>
            </div>
          </div>
          <p className="font-inter text-[12px] font-normal leading-[20px] tracking-[-0.005em] text-[#232228]">
            {testimonial.quote}
          </p>
        </div>
        {/* Desktop layout: centered column (avatar → quote → name → badge) */}
        <div className="hidden w-full flex-col items-center gap-[7px] md:flex">
          <Image
            src={testimonial.avatarUrl}
            alt={testimonial.name}
            width={77}
            height={77}
            className="h-[76px] w-[76px] shrink-0 rounded-full object-cover"
            unoptimized
          />
          <p className="w-full max-w-[336px] text-center font-inter text-[16px] font-normal leading-[20px] tracking-[-0.005em] text-[#232228] align-middle">
            {testimonial.quote}
          </p>
          <div className="flex flex-col items-center gap-[7px]">
            <span className="font-inter text-[16px] font-semibold leading-[20px] tracking-[-0.005em] text-center align-middle text-[#111827]">
              {testimonial.name}
            </span>
            <div className="flex w-fit items-center gap-[10px] rounded-[55px] bg-[#F3F3F3] px-[12px] py-[4px]">
              {testimonial.badgeIcon === "youtube" ? <YoutubeIcon /> : <LinkedinIcon />}
              <span className="font-inter text-[13px] font-normal leading-[20px] text-[#393F41]">
                {testimonial.badge}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Subheading + bullets — mobile only, green icons */}
      <div className="flex w-full flex-col gap-[18px] md:hidden">
        <p className="w-full text-center font-inter text-[18px] font-semibold leading-[30.72px] tracking-[-0.025em] text-[#232228]">
          {subheading}
        </p>
        <ul className="flex w-full flex-col gap-[3.41px]">
          {bulletPoints.map((point, i) => (
            <li key={i} className="flex items-start gap-[10px]">
              <span className="mt-[7px] shrink-0"><CheckIcon /></span>
              <span className="font-inter text-[16px] font-normal leading-[30.72px] tracking-[-0.025em] text-[#393F41]">
                {point}
              </span>
            </li>
          ))}
        </ul>
      </div>

    </div>
  );
};

// ── V2 Right panel (animation) ────────────────────────────────────────────────

const LottiePlayer = ({ src }: { src: string }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    import("lottie-web").then((lottie) => {
      if (!ref.current) return;
      const anim = lottie.default.loadAnimation({
        container: ref.current,
        renderer: "svg",
        loop: true,
        autoplay: true,
        path: src,
      });
      return () => anim.destroy();
    });
  }, [src]);

  return <div ref={ref} className="h-full w-full rounded-[15.96px]" />;
};

const SignupRightPanelV2 = ({ trustedAvatars, isUCECountry }: { trustedAvatars: StaticImageData[],isUCECountry: boolean }) => {
  const animationSrc = `${getCDNURL()}graphy-web/signup-page-animation.json`;
  const animationSrcIsUce = `${getCDNURL()}graphy-web/signup-page-animation-us.json`;

  return (
    <div className="flex w-full flex-col items-center gap-[32px]">
      <h2 className="w-full text-center font-inter text-[32px] font-[500] leading-[36px] tracking-[-0.025em] text-[#232228]">
        Your Business,{" "}
        <span className="inline-block bg-gradient-to-r text-[#FF905B] bg-clip-text font-[600] italic pr-[4px]">
          Run by AI
        </span>
      </h2>

      <div className="w-full h-[400px] overflow-hidden rounded-[15.96px] bg-transparent opacity-100">
        <LottiePlayer src={isUCECountry ? animationSrcIsUce : animationSrc} />
      </div>

      {trustedAvatars?.length > 0 && (
        <div className="flex flex-col items-center gap-[12px]" style={{ maxWidth: 316 }}>
          <p className="text-center font-inter text-[16px] font-normal leading-[24.38px] tracking-[-0.005em] text-[#555555]">
            Trusted by 2,00,000+ creators
          </p>
          <div className="flex items-center">
            {trustedAvatars.map((url, i) => (
              <div
                key={i}
                className={`
      relative overflow-hidden
      w-[44.99px] h-[44.99px]
      ${i === 0 ? 'ml-0' : '-ml-[10px]'}
      rounded-[67.16px]
      shadow-[0px_0px_0px_0px_rgba(0,0,0,0.25)]
    `}
                style={{ zIndex: trustedAvatars.length - i }}
              >
                <Image
                  src={url}
                  alt={`creator-${i}`}
                  fill
                  className="object-cover object-center"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Layout ────────────────────────────────────────────────────────────────────

const SignupPageLayout = ({
  navbarData,
  leftContent,
  rightPanelData,
  mobileBannerContent,
  version = "v1",
  isUCECountry,
}: SignupPageLayoutProps) => {
  return (
    <div className={`relative min-h-screen w-full overflow-hidden ${isMobile ? "bg-[linear-gradient(94.92deg,rgba(241,120,197,0.0528)_29.51%,rgba(247,178,145,0.12)_53.65%,rgba(247,178,145,0.12)_76%)] backdrop-blur-[576px]" : ""}`}>
      {!isMobile && (
        <Image
          src={SignupBg}
          alt=""
          aria-hidden="true"
          fill
          className="pointer-events-none object-cover object-left-top"
          priority
          quality={100}
        />)}
      {/* Page content */}
      <div className="relative z-10 mx-auto w-full max-w-[1180px] px-4 pb-[30px] md:pb-0">

        {/* Logo-only header — non-sticky, inside content flow */}
        <div className="flex items-center justify-center pt-[48px] md:justify-start">
          <Link href="https://graphy.com">
            <Image
              src="/images/graphy_logo_black.svg"
              width={100}
              height={32}
              alt="Graphy"
              className="h-[18px] w-[68px] md:h-[32px] md:w-[100px]"
            />
          </Link>
        </div>

        {/* Mobile banner */}
        {mobileBannerContent && (
          <div className="block w-full pt-2 md:hidden">
            {mobileBannerContent}
          </div>
        )}

        {/* Two-column row */}
        <div className="mt-6 flex w-full flex-col gap-6 md:mt-20 md:flex-row md:items-center md:gap-8 lg:gap-[140px]">

          <div className="w-full shrink-0 md:w-[380px] md:rounded-xl md:border md:border-[#878C904D] md:bg-white md:p-6 md:shadow-[0px_1px_26.8px_0px_rgba(0,0,0,0.12)] lg:w-[514px] lg:p-8">
            {leftContent}
          </div>

          {/* Right panel — v1 or v2 */}
          <div className="w-full min-w-0 flex-1 md:flex md:items-center md:justify-center lg:max-w-[526px]">
            <div className="mx-auto w-full max-w-[359px] md:max-w-none">
              {version === "v2"
                ? <SignupRightPanelV2 trustedAvatars={rightPanelData.trustedAvatars} isUCECountry={isUCECountry ?? false} />
                : <SignupRightPanelV1 data={rightPanelData} />
              }
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SignupPageLayout;